/**
 * services/adminConversationsApi.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin-only conversation moderation methods.
 *
 * METHODS:
 *   adminGetAllConversations(statusFilter)      — list all conversations
 *   adminGetConversationStats()                 — counts by status
 *   adminFlagConversation(conversationId)       — flag an offer thread
 *   adminUnflagConversation(conversationId)     — unflag an offer thread
 *   adminCompleteConversation(conversationId)   — mark as complete
 *   adminFlagDirectMessage(adId, userId)        — flag a DM thread
 *   adminUnflagDirectMessage(adId, userId)      — unflag a DM thread
 *   adminGetConversationMessages(conversationId) — read offer thread messages
 *   adminGetAdMessages(adId)                    — read DM thread messages
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { API_BASE_URL } from './apiUtils.js';

function _getToken() {
  return (
    localStorage.getItem('authToken')  ||
    localStorage.getItem('token')      ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('admin_token') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('authToken') ||
    sessionStorage.getItem('token')    ||
    sessionStorage.getItem('adminToken')
  );
}

function _adminHeaders(token) {
  const t = token || _getToken();
  return { Authorization: `Token ${t}`, 'Content-Type': 'application/json' };
}

// ── Methods — exported as a plain object, spread into ApiService in api.js ────
export const methods = {
async adminGetAllConversations(statusFilter = 'all') {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'Not authenticated', conversations: [] };
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      const url = `${API_BASE_URL}/admin/messages/${params.toString() ? '?' + params : ''}`;
      const res = await fetch(url, { headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || err.detail || `HTTP ${res.status}`, conversations: [] };
      }
      const data = await res.json();
      return { success: true, conversations: data.conversations || [], count: data.count || 0 };
    } catch (error) { return { success: false, error: error.message, conversations: [] }; }
  },

  async adminGetConversationStats() {
    try {
      const token = this._getToken();
      if (!token) return { success: false, stats: { total: 0, active: 0, completed: 0, flagged: 0 } };
      const res = await fetch(`${API_BASE_URL}/admin/messages/stats/`, {
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Failed to fetch stats');
      const data = await res.json();
      return { success: true, stats: data.stats || { total: 0, active: 0, completed: 0, flagged: 0 } };
    } catch { return { success: false, stats: { total: 0, active: 0, completed: 0, flagged: 0 } }; }
  },

  async adminFlagConversation(conversationId) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const res = await fetch(`${API_BASE_URL}/admin/messages/${conversationId}/flag/`, {
        method: 'POST', headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || 'Failed to flag conversation' };
      }
      const data = await res.json();
      return { success: true, message: data.message || 'Conversation flagged successfully' };
    } catch (error) { return { success: false, error: error.message }; }
  },

  async adminUnflagConversation(conversationId) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const res = await fetch(`${API_BASE_URL}/admin/messages/${conversationId}/unflag/`, {
        method: 'POST', headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || 'Failed to unflag conversation' };
      }
      const data = await res.json();
      return { success: true, message: data.message || 'Conversation unflagged successfully' };
    } catch (error) { return { success: false, error: error.message }; }
  },

  async adminCompleteConversation(conversationId) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const res = await fetch(`${API_BASE_URL}/admin/messages/${conversationId}/complete/`, {
        method: 'POST', headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || 'Failed to complete conversation' };
      }
      const data = await res.json();
      return { success: true, message: data.message || 'Conversation marked as completed' };
    } catch (error) { return { success: false, error: error.message }; }
  },

  async adminFlagDirectMessage(adId, userId) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const res = await fetch(`${API_BASE_URL}/admin/direct-messages/${adId}/${userId}/flag/`, {
        method: 'POST', headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || `HTTP ${res.status}` };
      }
      const data = await res.json();
      return { success: true, message: data.message || 'Thread flagged successfully', is_flagged: true };
    } catch (error) { return { success: false, error: error.message }; }
  },

  async adminUnflagDirectMessage(adId, userId) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const res = await fetch(`${API_BASE_URL}/admin/direct-messages/${adId}/${userId}/unflag/`, {
        method: 'POST', headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || `HTTP ${res.status}` };
      }
      const data = await res.json();
      return { success: true, message: data.message || 'Flag removed successfully', is_flagged: false };
    } catch (error) { return { success: false, error: error.message }; }
  },
// ADMIN — CONVERSATION MESSAGES

  async adminGetConversationMessages(conversationId) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, messages: [] };
      const res = await fetch(`${API_BASE_URL}/admin/messages/${conversationId}/messages/`, {
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        return { success: true, messages: data.messages || data.results || (Array.isArray(data) ? data : []), is_flagged: data.is_flagged ?? false };
      }
      // fallback URL
      const fallback = await fetch(`${API_BASE_URL}/admin/conversations/${conversationId}/messages/`, {
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (fallback.ok) {
        const data = await fallback.json();
        return { success: true, messages: data.messages || data.results || (Array.isArray(data) ? data : []), is_flagged: data.is_flagged ?? false };
      }
      const err = await res.json().catch(() => ({}));
      return { success: false, messages: [], error: err.error || err.detail || `HTTP ${res.status}` };
    } catch (error) { return { success: false, messages: [], error: error.message }; }
  },

  async adminGetAdMessages(adId) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, messages: [] };
      const res = await fetch(`${API_BASE_URL}/admin/ads/${adId}/messages/`, {
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, messages: [], error: err.error || `HTTP ${res.status}` };
      }
      const data = await res.json();
      return { success: true, messages: data.messages || data.results || (Array.isArray(data) ? data : []) };
    } catch (error) { return { success: false, messages: [], error: error.message }; }
  }

  // PROFILE — get, update, password reset, submit verification
};

